import { ProjetSignature } from "@src/App";
import { Stepbar } from "@src/components/stepbar/Stepbar";
import { gatewayAPI } from "@src/GatewayAPI";
import axios from "axios";
import {
  Form,
  InputMask,
  InputText,
  useFormConfig,
} from "@lya-protect/lya-protect-form-library/dist/FormAPI";
import {
  Button,
  Emphasis,
  FormAction,
  FormBloc,
  FormField,
  FormGrid,
  FormNotice,
  FormRow,
  FormSubTitle,
  FormTitle,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import { zod } from "@lya-protect/lya-protect-form-library/dist/Exports";
import { captureException } from "@sentry/react";
import { useState } from "react";
import { Collapse } from "react-collapse";
import { NavButton } from "@src/components/navButton/NavButton";
import "./mandatPrelevement.scss";
import { Card } from "@src/components/card/Card";
import { InfoBloc } from "@src/components/infoBloc/InfoBloc";
import { displayToast } from "@src/components/toaster/Toaster";
import { LoaderOverlay } from "@src/components/loaderOverlay/LoaderOverlay";
import { CONSENTEMENT_AVERTISSEMENT } from "@src/Steps/subscribe/consents/Consent";
import { MicroStep, StepProps } from "../../step/Steps";
import {
  CONSENTEMENT_CONTACT,
  CONSENTEMENT_EXCLUSIONS_PRODUIT,
  CONSENTEMENT_FAUSSE_DECLA,
  CONSENTEMENT_PRODUIT,
  CONSENTEMENT_REGLEMENT_CG,
  CONSENTEMENT_RENONCER_CONTRAT,
  CONSENTEMENT_RESILIATION,
  DECLARATION_VERACITE,
} from "../consentSouscription/ConsentSouscription";

export type MandatPrelevementData =
  | {
      name: string;
      surname: string;
      address: string;
      postalCode: string;
      city: string;
      iban: string;
      bic: string;
    }
  | undefined;
type MandatPrelevementProps = StepProps<MandatPrelevementData>;

function cleanMaskedInput(val: unknown) {
  if (typeof val !== "string") return undefined;
  return val.replace(/\s/g, "");
}

const schema = zod.object({
  name: zod.string({ required_error: "Champ requis" }).min(1, "Champ requis"),
  surname: zod
    .string({ required_error: "Champ requis" })
    .min(1, "Champ requis"),
  address: zod
    .string({ required_error: "Champ requis" })
    .min(1, "Champ requis"),
  postalCode: zod
    .string({ required_error: "Champ requis" })
    .min(1, "Champ requis")
    .regex(/^\d{5}/, { message: "Code invalide" }),
  city: zod.string({ required_error: "Champ requis" }).min(1, "Champ requis"),
  bic: zod.preprocess(
    cleanMaskedInput,
    zod
      .string({ required_error: "Champ requis" })
      .min(1, "Champ requis")
      .regex(/^\w{8}$|\w{11}$/, "Code BIC invalide")
  ),
  iban: zod
    .string({ required_error: "Champ requis" })
    .max(34)
    .refine(async (val) => {
      try {
        const res = await axios.get<{ isValid: false }>(
          `//${process.env.VITE_URL_GATEWAY}/iban/check/${val}`
        );
        return res.data.isValid;
      } catch (err) {
        return false;
      }
    }, "Numéro IBAN invalide"),
});

type FormValues = zod.infer<typeof schema>;

export function MandatPrelevement({
  data,
  projectId,
  stepsData,
  setProjetSignature,
  changeStep,
  projetSouscription,
}: MandatPrelevementProps) {
  const [infoBlocIsOpen, setInfoBlocIsOpen] = useState(false);
  const formConfig = useFormConfig<typeof schema, FormValues>({
    schema,
    defaultValues: {
      ...data,
      name: data?.name ?? projetSouscription?.assure.name,
      surname: data?.surname ?? projetSouscription?.assure.surname,
      address: data?.address ?? projetSouscription?.assure.address,
      postalCode: data?.address ?? projetSouscription?.assure.zipcode,
      city: data?.city ?? projetSouscription?.assure.city,
    },
    mode: "onChange",
  });

  const postalCode = formConfig.watch("postalCode");

  if (!projectId || !stepsData.CONSENT || !stepsData.CONSENT_SOUSCRIPTION)
    return null;

  const handleSubmit = async (submittedData: FormValues) => {
    const consentements: { type: string; description: string }[] = [];
    const champsPerso: { codeField: string; value: boolean }[] = [
      {
        codeField: "ALLOC_BENEFICIAIRE",
        value: stepsData.BENEFICIAIRES.hasEqualShares
          ? stepsData.BENEFICIAIRES.hasEqualShares
          : false,
      },
    ];

    if (
      stepsData.CONSENT?.consentSincere &&
      stepsData.CONSENT?.consentRisque &&
      stepsData.CONSENT?.consentModify &&
      stepsData.CONSENT?.consentNoComplete &&
      stepsData.CONSENT?.consentLaw
    ) {
      consentements.push({
        type: "CONSENTEMENT_AVERTISSEMENT",
        description: CONSENTEMENT_AVERTISSEMENT,
      });
    }
    if (stepsData.CONSENT_SOUSCRIPTION?.consentDocInfoProduit) {
      consentements.push({
        type: "CONSENTEMENT_PRODUIT",
        description: CONSENTEMENT_PRODUIT,
      });
    }
    if (stepsData.CONSENT_SOUSCRIPTION?.consentExclusionReg) {
      consentements.push({
        type: "CONSENTEMENT_EXCLUSIONS_PRODUIT",
        description: CONSENTEMENT_EXCLUSIONS_PRODUIT,
      });
    }
    if (stepsData.CONSENT_SOUSCRIPTION?.consentFausseDec) {
      consentements.push({
        type: "CONSENTEMENT_FAUSSE_DECLA",
        description: CONSENTEMENT_FAUSSE_DECLA,
      });
    }
    if (stepsData.CONSENT_SOUSCRIPTION?.consentRenoncerContrat) {
      consentements.push({
        type: "CONSENTEMENT_RENONCER_CONTRAT",
        description: CONSENTEMENT_RENONCER_CONTRAT,
      });
    }
    if (stepsData.CONSENT_SOUSCRIPTION?.consentResiliation) {
      consentements.push({
        type: "CONSENTEMENT_RESILIATION",
        description: CONSENTEMENT_RESILIATION,
      });
    }
    if (stepsData.CONSENT_SOUSCRIPTION?.consentVeracite) {
      consentements.push({
        type: "CONSENTEMENT_VERACITE",
        description: DECLARATION_VERACITE,
      });
    }
    if (stepsData.CONSENT_SOUSCRIPTION?.consentTermesProduit) {
      consentements.push({
        type: "CONSENTEMENT_REGLEMENT_CG",
        description: CONSENTEMENT_REGLEMENT_CG,
      });
    }
    if (stepsData.CONSENT_SOUSCRIPTION?.consentContact) {
      consentements.push({
        type: "CONSENTEMENT_CONTACT",
        description: CONSENTEMENT_CONTACT,
      });
    }

    try {
      const res = await axios.post<ProjetSignature>(
        `//${process.env.VITE_URL_GATEWAY}/souscrire/${projectId}`,
        {
          consentements,
          numeroSecu: stepsData.NUM_SECU?.numSecu,
          paysNaissance: stepsData.NUM_SECU?.countryOfBirth,
          villeNaissance: stepsData.NUM_SECU?.cityOfBirth,
          beneficiaires: stepsData.BENEFICIAIRES?.beneficiaries
            ? stepsData.BENEFICIAIRES?.beneficiaries.map((beneficiaire) => ({
                beneficiaire: {
                  type: "personnePhysique",
                  civiliteCode: "MONSIEUR:CIVILITE",
                  civilite: "Monsieur",
                  nomDeFamille: beneficiaire.familyName,
                  nom: beneficiaire.usageName || beneficiaire.familyName,
                  prenom: beneficiaire.firstName,
                  naissanceDate: beneficiaire?.birthDate
                    ? beneficiaire.birthDate.toISOString()
                    : null,
                  codePostalNaissance: beneficiaire.birthZipcode,
                  villeNaissance: beneficiaire.birthPlace,
                  adresse: {
                    voie: beneficiaire.address,
                    complement: beneficiaire.addressComplement,
                    codePostal: beneficiaire.zipcode,
                    ville: beneficiaire.city,
                  },
                  mobile: beneficiaire.phone,
                  mail: beneficiaire.email,
                },
                relationCode: beneficiaire.kinship,
                relationDetail: beneficiaire.kinshipDetail,
              }))
            : undefined,
          rib: {
            nom: submittedData.name,
            prenom: submittedData.surname,
            voie: submittedData.address,
            codePostal: submittedData.postalCode,
            ville: submittedData.city,
            iban: cleanMaskedInput(submittedData.iban),
            bic: cleanMaskedInput(submittedData.bic),
          },
          champsSupplementaireParcours: champsPerso,
          ppe: {
            isPPE: stepsData.POLITIQUEMENT_EXPOSEE?.assurePPE || undefined,
            details: `${
              stepsData.POLITIQUEMENT_EXPOSEE?.assurePPEFonction ?? ""
            } ${
              stepsData.POLITIQUEMENT_EXPOSEE?.assurePPECessation
                ? stepsData.POLITIQUEMENT_EXPOSEE.assurePPECessation.toLocaleDateString(
                    "fr-FR"
                  )
                : ""
            }`,
            isProchePPE:
              stepsData.POLITIQUEMENT_EXPOSEE?.prochePPE || undefined,
            detailsProche: `${
              stepsData.POLITIQUEMENT_EXPOSEE?.prochePPEFonction ?? ""
            } ${
              stepsData.POLITIQUEMENT_EXPOSEE?.assurePPECessation
                ? stepsData.POLITIQUEMENT_EXPOSEE.assurePPECessation.toLocaleDateString(
                    "fr-FR"
                  )
                : ""
            }`,
          },
        }
      );
      setProjetSignature(res.data);
      changeStep("PIECES_JUSTIFICATIVES", "forward");
    } catch (err) {
      captureException(err);
      console.error(err);
      displayToast("Une erreur inconnue est survenue", "error");
      throw err;
    }
  };

  return (
    <>
      <LoaderOverlay isLoading={formConfig.formState.isSubmitting} />
      <div className="mandat-prelevement-step">
        <div className="stepper__centered-container">
          <div className="stepper__centered-content step__grid step__grid--padding-bottom">
            <Stepbar activeMicroStep={MicroStep.Payment} />
            <NavButton
              onClick={() => {
                changeStep("CONSENT_SOUSCRIPTION", "backward");
              }}
            >
              Précédent
            </NavButton>
            <Form
              className="mandat-prelevement-step__form"
              formConfig={formConfig}
              onSubmit={handleSubmit}
            >
              <FormGrid>
                <FormTitle>
                  <h1>
                    Complétez votre mandat de{" "}
                    <Emphasis>prélèvement SEPA</Emphasis>
                  </h1>
                </FormTitle>
                <FormNotice>
                  <p>
                    <Emphasis>*</Emphasis> Champs obligatoires
                  </p>
                </FormNotice>
                <InfoBloc
                  className="mandat-prelevement-step__info-bloc"
                  variant="accent"
                >
                  <p>
                    En signant ce formulaire de mandat, vous autorisez la
                    mutuelle TUTÉLAIRE à envoyer des instructions à votre banque
                    pour débiter votre compte, et votre banque à débiter votre
                    compte conformément aux instructions de la mutuelle
                    TUTÉLAIRE.
                  </p>
                  <Collapse isOpened={infoBlocIsOpen}>
                    <p>
                      Vous bénéficiez du droit d&apos;être remboursé par votre
                      banque selon les conditions décrites dans la convention
                      que vous avez passé avec elle. Une demande de
                      remboursement doit être présentée :
                    </p>
                    <ul>
                      <li>
                        Dans les 8 semaines suivant la date de débit de votre
                        compte pour un prélèvement autorisé ;
                      </li>
                      <li>
                        Sans tarder et au plus tard dans les 13 mois en cas de
                        prélèvement non autorisé ;
                      </li>
                    </ul>
                    <p>
                      Vos droits concernant le présent mandat sont expliqués
                      dans un document que vous pouvez obtenir auprès de votre
                      banque.
                    </p>
                  </Collapse>
                  <div className="mandat-prelevement-step__collapse-button-wrapper">
                    <button
                      data-isopen={infoBlocIsOpen}
                      className="mandat-prelevement-step__collapse-button"
                      type="button"
                      onClick={() => {
                        setInfoBlocIsOpen(!infoBlocIsOpen);
                      }}
                    >
                      {infoBlocIsOpen ? "Masquer" : "En savoir plus"}
                    </button>
                  </div>
                </InfoBloc>
                <FormSubTitle>
                  <h2>Titulaire du compte</h2>
                </FormSubTitle>
                <FormRow>
                  <FormField>
                    <InputText<FormValues> name="name" label="Nom" required />
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField>
                    <InputText<FormValues>
                      name="surname"
                      label="Prénom"
                      required
                    />
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField>
                    <InputText<FormValues>
                      name="address"
                      label="Adresse postale"
                      required
                    />
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField
                    size="half"
                    className="mandat-prelevement-step__autocomplete-field"
                  >
                    <InputText
                      name="postalCode"
                      label="Code postal"
                      required
                      placeholder="Ex: 75013"
                      autoComplete={{
                        displayLabel: (result: string) =>
                          `${postalCode ?? ""} - ${result}`,
                        fetchResults: async () => {
                          if (!postalCode || postalCode.length !== 5) return [];
                          return gatewayAPI.axios
                            .get<string[]>(`geo/communes/search/${postalCode}`)
                            .then((res) => res.data)
                            .catch(() => []);
                        },
                        onSelect: (newCity) => {
                          formConfig.setValue(`city`, newCity, {
                            shouldValidate: true,
                            shouldTouch: true,
                          });
                        },
                        focusAfterSelect: `city`,
                      }}
                    />
                  </FormField>
                  <FormField size="half">
                    <InputText<FormValues>
                      name="city"
                      label="Ville"
                      required
                      placeholder="Ex: Paris"
                    />
                  </FormField>
                </FormRow>
                <FormSubTitle>
                  <h2>Compte à débiter</h2>
                </FormSubTitle>
                <FormRow>
                  <FormField>
                    <InputMask
                      className="mandat-prelevement-step__uppercase-input"
                      name="iban"
                      mask="**** **** **** **** **** **** ***"
                      maskPlaceholder=""
                      label="IBAN"
                      required
                    />
                  </FormField>
                </FormRow>
                <FormRow>
                  <FormField>
                    <InputMask
                      className="mandat-prelevement-step__uppercase-input"
                      name="bic"
                      label="BIC"
                      mask="**** ** ** ***"
                      onChange={(nextValue, isTouched) => {
                        formConfig.setValue("bic", nextValue.toUpperCase(), {
                          shouldValidate: isTouched,
                        });
                      }}
                      required
                    />
                  </FormField>
                </FormRow>

                <FormSubTitle>
                  <h2>Informations sur le créancier</h2>
                </FormSubTitle>
                <FormBloc>
                  <Card
                    variant="accent"
                    className="mandat-prelevement-step-style"
                  >
                    <h2 className="mandat-prelevement-step__card-title">
                      Mutuelle Tutélaire
                    </h2>
                    <div className="mandat-prelevement-step__info-grid">
                      <div className="mandat-prelevement-step__info-item">
                        <p>Adresse postale</p>
                        <p>
                          157 avenue de France
                          <br />
                          75013 Paris
                        </p>
                      </div>
                      <div className="mandat-prelevement-step__info-item">
                        <p>Identifiant créancier SEPA</p>
                        <p>FR88ZZZ386329</p>
                      </div>
                      <div className="mandat-prelevement-step__info-item">
                        <p>Type de paiement</p>
                        <p>Paiement mensuel récurrent / répétitif</p>
                      </div>
                    </div>
                  </Card>
                </FormBloc>
                <FormAction>
                  <Button
                    type="submit"
                    disabled={
                      !formConfig.formState.isValid ||
                      formConfig.formState.isSubmitting ||
                      formConfig.formState.isSubmitSuccessful
                    }
                  >
                    Suivant
                  </Button>
                </FormAction>
              </FormGrid>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
}
